/**
 * Employee-portal messages — German (reference language). This is the shape
 * (`Messages`) every other employee language file is type-checked against, so
 * add new keys here first. Filled in incrementally as views are migrated;
 * Profile.vue is the first (Phase 4).
 */
const de = {
  profile: {
    heading: 'Einstellungen',
    subtitle: 'Verwalten Sie Ihr Profil, Ihre Sicherheit und Benachrichtigungen.',
    tabs: {
      profile: 'Profil',
      security: 'Sicherheit',
      notifications: 'Benachrichtigungen',
    },
    card: {
      profile: 'Profil',
      account: 'Konto',
      language: 'Sprache',
      security: 'Passwort ändern',
    },
    fields: {
      username: 'Benutzername',
      email: 'E-Mail',
      extension: 'Nebenstelle',
      extensionHint: 'Zwei Ziffern.',
      description: 'Beschreibung',
      phone1: 'Telefon 1',
      phone2: 'Telefon 2',
      mobile: 'Mobil',
      creationDate: 'Erstellt',
      lastLogin: 'Letzte Anmeldung',
      neverLoggedIn: 'Nie',
    },
    directoryNote: 'Benutzername und E-Mail stammen aus dem Verzeichnis und können hier nicht geändert werden.',
    save: {
      submit: 'Speichern',
      unsaved: 'Nicht gespeicherte Änderungen',
      saved: 'Profil gespeichert',
      savedDetail: 'Ihre Profiländerungen wurden gespeichert.',
      saveFailed: 'Ihr Profil konnte nicht gespeichert werden. Bitte erneut versuchen.',
      loadFailed: 'Ihr Profil konnte nicht geladen werden. Bitte erneut versuchen.',
    },
    account: {
      portal: 'Portal',
      roles: 'Rollen',
      noRoles: 'Keine Rollen zugewiesen.',
    },
    portal: {
      employee: 'Mitarbeiter-Portal',
      customer: 'Kunden-Portal',
    },
    language: {
      description: 'Legt die Sprache der Benutzeroberfläche für Ihr Konto fest.',
      saved: 'Sprache geändert',
      savedDetail: 'Ihre Spracheinstellung wurde gespeichert.',
      saveFailed: 'Die Sprache konnte nicht geändert werden. Bitte erneut versuchen.',
    },
    password: {
      ldapNote:
        'Ihr Passwort wird über das Verzeichnis (LDAP) verwaltet und kann hier nicht geändert werden.',
      current: 'Aktuelles Passwort',
      next: 'Neues Passwort',
      confirm: 'Passwort bestätigen',
      minChars: 'Mindestens {min} Zeichen.',
      mismatch: 'Die Passwörter stimmen nicht überein.',
      tooShort: 'Das neue Passwort muss mindestens {min} Zeichen lang sein.',
      submit: 'Passwort ändern',
      changeFailed: 'Änderung fehlgeschlagen. Bitte erneut versuchen.',
      saved: 'Passwort geändert',
      savedDetail: 'Ihr Passwort wurde aktualisiert.',
    },
    notifications: {
      unavailable: 'Benachrichtigungseinstellungen sind noch nicht verfügbar.',
    },
  },
  dashboard: {
    // "Whitelist" / "Blacklist" are domain terms used verbatim in both languages.
    whitelist: 'Whitelist',
    blacklist: 'Blacklist',
    stats: {
      callsToday: 'Anrufe heute',
      numbers: 'Nummern',
    },
    check: {
      title: 'Telefonnummer prüfen',
      submit: 'Nummer prüfen',
      failed: 'Prüfung fehlgeschlagen. Bitte erneut versuchen.',
      resultTitle: 'Prüfergebnis',
      checkedNumber: 'Geprüfte Nummer',
      noEntry: 'Kein Eintrag',
      close: 'Schließen',
      whitelistWithHint: 'Whitelist ({hint})',
      blacklistWithHint: 'Blacklist ({hint})',
    },
    direction: {
      all: 'Alle',
      inbound: 'Eingehend',
      outbound: 'Ausgehend',
      filterLabel: 'Richtung filtern',
    },
    match: {
      direct: 'direkter Eintrag',
      customer: 'Kunde',
      user: 'Benutzer',
    },
    recent: {
      title: 'Letzte Anrufe',
      searchPlaceholder: 'Nummer, Kontakt oder Kunde…',
      ownOnly: 'Nur eigene Anrufe',
      empty: 'Keine Anrufe gefunden.',
      columns: {
        timestamp: 'Zeitpunkt',
        direction: 'Richtung',
        number: 'Rufnummer',
        contact: 'Kontakt',
        customer: 'Kunde',
        agent: 'Angenommen von',
        duration: 'Dauer',
        action: 'Aktion',
      },
    },
  },
  nav: {
    overview: 'Überblick',
    dashboard: 'Dashboard',
    customersGroup: 'Kunden',
    customers: 'Kunden',
    whitelist: 'Anruffilter',
    profile: 'Profil',
    home: 'Start',
    brandSub: 'Mitarbeiter-Konsole',
    orgPlan: 'Mitarbeiterzugang',
  },
  customers: {
    title: 'Kunden',
    subtitle: 'Kundenstammdaten – schreibgeschützt, gepflegt über die WISO MeinBüro.',
    results: '{count} Kunden',
    empty: 'Keine Kunden gefunden.',
    loadFailed: 'Kunden konnten nicht geladen werden.',
    open: 'Kunde öffnen',
    columns: {
      number: 'Nummer',
      name: 'Name',
      street: 'Straße',
      zip: 'PLZ',
      city: 'Ort',
      type: 'Typ',
    },
    filter: {
      number: 'Nummer filtern',
      name: 'Name filtern',
      street: 'Straße filtern',
      zip: 'PLZ filtern',
      city: 'Ort filtern',
      typeAll: 'Alle Typen',
    },
    type: {
      CUSTOMER: 'Kunde',
      SUPPLIER: 'Lieferant',
      INTERESTED_PARTY: 'Interessent',
      UNKNOWN: 'Unbekannt',
    },
    detail: {
      title: 'Kunde',
      subtitle: 'Kundennummer {number}',
      back: 'Zurück zur Liste',
      loadFailed: 'Der Kunde konnte nicht geladen werden.',
      detailsFailed: 'Einige Detaildaten konnten nicht geladen werden.',
      tabs: {
        overview: 'Übersicht',
        contacts: 'Ansprechpartner',
        profile: 'Profil',
      },
      card: {
        contact: 'Kontakt',
        master: 'Stammdaten',
      },
      fields: {
        country: 'Land',
        phone1: 'Telefon 1',
        phone2: 'Telefon 2',
        mobile: 'Mobil',
        fax: 'Fax',
        email: 'E-Mail',
        website: 'Webseite',
        zipCity: 'PLZ, Ort',
      },
      ticket: {
        new: 'Neues Ticket',
        soonTitle: 'Bald verfügbar',
        soonDetail: 'Das Erstellen von JIRA-Tickets ist in einer späteren Version verfügbar.',
      },
      activity: {
        title: 'Anrufaktivität',
        range: 'Letzte 12 Monate',
        calls: 'Anrufe / Monat',
        duration: 'Dauer (Min) / Monat',
        callsTooltip: 'Anrufe: {count}',
        durationTooltip: 'Dauer: {count} Min',
      },
      contacts: {
        empty: 'Keine Ansprechpartner vorhanden.',
        columns: {
          nr: 'Nr.',
          name: 'Name',
          position: 'Position',
        },
      },
      profile: {
        cardSystem: 'System & ERP',
        cardScope: 'Umfang & Lizenzen',
        cardSupport: 'Betreuung',
        erpSystem: 'ERP-System',
        databaseType: 'SQL-DB',
        serverOsName: 'Serverbetriebssystem',
        databaseVersion: 'SQL-DB Version',
        oemVersion: 'OEM-Version',
        installedVersionLive: 'Inst. Version LIVE',
        remoteMaintenance: 'Fernwartungszugang',
        lastUpdateLive: 'Letztes Update LIVE',
        employeeCount: 'Mitarbeiter',
        industry: 'Branche',
        workplaces: 'Arbeitsplätze',
        systems: 'Systeme',
        s4pSolutions: 'S4P-Lösungen',
        maintenanceContract: 'Wartungsvertrag',
        maintenanceActive: 'Aktiv',
        maintenanceInactive: 'Inaktiv',
        licenses: 'Lizenzen',
        salesPerson: 'S4P-Vertriebler',
        projectManager: 'S4P-Projektleiter',
        projectTeam: 'S4P-Projektteam',
        installationHint: 'Installationshinweis',
      },
    },
  },
  whitelist: {
    title: 'Whitelist',
    placeholder: 'Beispielhafte Whitelist-Verwaltung.',
  },
  callFilter: {
    whitelistTitle: 'Neuer Whitelist-Eintrag',
    blacklistTitle: 'Neuer Blacklist-Eintrag',
    editWhitelistTitle: 'Whitelist-Eintrag bearbeiten',
    editBlacklistTitle: 'Blacklist-Eintrag bearbeiten',
    explainWhitelist: 'Anrufe von diesem Ziel werden immer durchgestellt.',
    explainBlacklist: 'Anrufe von diesem Ziel werden automatisch blockiert.',
    target: 'Ziel',
    targetCustomer: 'Kunde',
    targetNumber: 'Nummer',
    customer: 'Kunde',
    customerEmpty: 'Keine Kunden gefunden.',
    customerLocked: 'Der Kunde eines bestehenden Eintrags kann nicht mehr geändert werden.',
    noCustomer: 'Kein Kunde gewählt',
    columns: {
      number: 'Kundennummer',
      name: 'Name',
    },
    number: 'Rufnummer',
    numberPlaceholder: 'z. B. +49 89 416 140 60',
    numberHint: 'Komplette Nummer mit Landesvorwahl. Ohne Vorwahl wird eine deutsche Nummer angenommen.',
    numberLocked: 'Die Nummer eines bestehenden Eintrags kann nicht mehr geändert werden.',
    noNumber: 'Keine Nummer',
    preview: 'Vorschau',
    details: 'Details',
    description: 'Beschreibung',
    descriptionPlaceholder: 'z. B. Grund für den Eintrag…',
    optional: '(optional)',
    validity: 'Gültigkeit',
    ruleActive: 'Regel aktiv',
    expiration: 'Ablaufdatum',
    expirationHint: 'Der Eintrag läuft am Ende des gewählten Tages ab.',
    active: 'Aktiv',
    cancel: 'Abbrechen',
    save: 'Speichern',
    numberRequired: 'Bitte geben Sie eine Telefonnummer an.',
    customerRequired: 'Bitte wählen Sie einen Kunden aus.',
    expirationRequired: 'Bitte geben Sie ein Ablaufdatum an.',
    whitelistCreated: 'Whitelist-Eintrag erstellt',
    blacklistCreated: 'Blacklist-Eintrag erstellt',
    whitelistUpdated: 'Whitelist-Eintrag aktualisiert',
    blacklistUpdated: 'Blacklist-Eintrag aktualisiert',
    saveFailed: 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
    list: {
      title: 'Anruffilter',
      counts: '{whitelist} Whitelist · {blacklist} Blacklist',
      newEntry: 'Neuer Eintrag',
      newWhitelist: 'Whitelist-Eintrag',
      newBlacklist: 'Blacklist-Eintrag',
      searchPlaceholder: 'Nummer oder Beschreibung…',
      kindLabel: 'Nach Liste filtern',
      kind: {
        all: 'Alle',
        whitelist: 'Whitelist',
        blacklist: 'Blacklist',
      },
      statusLabel: 'Status',
      statusAll: 'Alle',
      results: '{count} Einträge',
      empty: 'Noch keine Filtereinträge vorhanden.',
      loadFailed: 'Die Einträge konnten nicht geladen werden. Bitte erneut versuchen.',
      columns: {
        kind: 'Typ',
        number: 'Nummer',
        description: 'Beschreibung',
        status: 'Status',
        expiration: 'Läuft ab',
        created: 'Gültig seit',
        actions: 'Aktionen',
      },
      status: {
        active: 'Aktiv',
        inactive: 'Inaktiv',
        expired: 'Abgelaufen',
      },
      edit: 'Bearbeiten',
      delete: 'Löschen',
      deleteHeader: 'Eintrag löschen?',
      deleteMessage: '{number} aus der Liste entfernen? Dies kann nicht rückgängig gemacht werden.',
      deleteConfirm: 'Löschen',
      deleteCancel: 'Abbrechen',
      deleted: 'Eintrag gelöscht',
      deleteFailed: 'Der Eintrag konnte nicht gelöscht werden. Bitte erneut versuchen.',
    },
  },
};

export type Messages = typeof de;
export default de;
